// Complete Mock data for God Has a Plan Landing Page

export const heroContent = {
  headline: "The Three-Platform Path to $6,000+ Monthly Income",
  subheadline: "A Practical Stability Roadmap",
  description: "Turn your assets, time, and energy into diversified income streams with proven strategies from Turo, Turno, and Roomster.",
  cta: "GET YOUR GUIDE NOW - $50",
  socialProof: "Join 5,000+ People Building Wealth"
};

export const problemStatement = {
  title: "The Reality of Single-Income Dependency",
  content: "In 2025, relying on a single job or income source has become increasingly risky. Economic shifts, industry disruption, and unexpected life events can eliminate income streams overnight. Yet most financial advice for ordinary people boils down to \"get a better job\" or \"start a business\"—both of which require resources most people don't have.",
  solution: "This guide offers a different approach: intelligent diversification across three proven platforms that require minimal startup capital and leverage assets you likely already have."
};

export const platformData = [
  {
    id: 'turo',
    name: 'TURO',
    subtitle: 'Vehicle Income',
    income: '$450-1,050/week',
    icon: 'Car',
    description: 'If you own a vehicle, you own an underutilized asset. A car sitting in your driveway represents lost income.',
    benefits: [
      'How to acquire reliable vehicles on tight budgets ($4,000-10,000 range)',
      'Six vehicle acquisition methods: auctions, BHPH dealerships, private sales, leasing, owner financing, and employee programs',
      'Hosting strategies that generate $900-1,500/month per vehicle',
      'Pricing optimization for maximum occupancy and revenue',
      'Multi-vehicle scaling roadmap (1 vehicle → 8+ vehicle fleet)',
      'Year-by-year financial projections showing path to $42,000+ annual income'
    ]
  },
  {
    id: 'turno',
    name: 'TURNO',
    subtitle: 'Professional Cleaning',
    income: '$2,000-4,500/month',
    icon: 'Sparkles',
    description: 'Professional cleaning is in constant demand and requires minimal startup investment. Unlike retail or service businesses, you\'re not competing on location—you\'re competing on quality and reliability.',
    benefits: [
      'How to start with under $750 in supplies',
      'Fair pricing strategies based on your actual hourly needs',
      'Step-by-step bidding system that gets jobs accepted',
      'Real job economics: Earn $30-45/hour with proper pricing',
      'How to scale from solo work to managing a 2-3 person cleaning team',
      'Monthly income planning and tax optimization strategies'
    ]
  },
  {
    id: 'roomster',
    name: 'ROOMSTER',
    subtitle: 'Housing Income',
    income: '$1,000-1,200/month',
    icon: 'Home',
    description: 'Your living space represents untapped income. Whether you have a spare bedroom, basement unit, or accessory dwelling unit, Roomster lets you monetize that space.',
    benefits: [
      'How to legally list a room in your home on Roomster',
      'Screening tenants to minimize risk and conflict',
      'Setting competitive pricing that attracts quality renters',
      'Building occupancy rates and managing renewals',
      'How this fits with the other two income streams',
      'Expected income: $1,000-1,500/month depending on location'
    ]
  }
];

export const featuresData = [
  { title: 'Realistic Financial Projections', desc: 'Not wishful thinking; actual monthly/annual earnings with detailed calculations' },
  { title: 'Step-by-Step Action Plans', desc: 'Not vague advice; exact processes with screenshots, templates, and examples' },
  { title: 'Cost Breakdowns', desc: 'Every recommendation includes actual dollar amounts, not estimates' },
  { title: 'Real Market Data', desc: 'Pricing based on 2025 actual platform rates in major US markets' },
  { title: 'Tax & Legal Guidance', desc: 'How to structure earnings, deductions, and legal considerations' },
  { title: 'Scaling Roadmaps', desc: 'From beginner to professional operator on each platform' },
  { title: 'Biblical Wisdom Integration', desc: 'Faith-based perspective on work, stewardship, and prosperity' },
  { title: 'Complete Startup Costs', desc: 'What you actually need to invest to get started ($500-6,500 total)' }
];

export const whyThisWorks = [
  {
    title: 'Diversification Without Complexity',
    desc: 'Instead of juggling 5+ side hustles on different platforms, you\'re building expertise and efficiency in three proven, accessible platforms.'
  },
  {
    title: 'Leverage Existing Assets',
    desc: 'You\'re not creating new assets—you\'re monetizing what you already have: a vehicle, your labor, and living space.'
  },
  {
    title: 'Scalability With Minimal Risk',
    desc: 'Start small on each platform (one vehicle, a few cleaning jobs, one room), test the model, then scale systematically.'
  },
  {
    title: 'Realistic Timelines and Returns',
    desc: 'The guide provides honest month-by-month projections, not unrealistic overnight-wealth promises.'
  },
  {
    title: 'Income Security Through Diversification',
    desc: 'If one platform has a bad month, two others continue generating income. If one platform changes policies, you\'re not dependent on it.'
  }
];

export const whoIsThisFor = [
  'People seeking additional income alongside their primary job',
  'Those wanting to diversify away from single-income dependency',
  'Gig economy workers looking for multiple platforms',
  'Anyone with a vehicle, spare room, or willingness to work',
  'People building toward financial stability and long-term wealth',
  'Individuals seeking work flexibility and control over schedule'
];

export const financialProjections = [
  { timeline: 'Month 1-2', turo: '$525', turno: '$1,600', roomster: '$1,000', total: '$3,125', description: 'Starting Out' },
  { timeline: 'Month 3-4', turo: '$810', turno: '$2,400', roomster: '$1,000', total: '$4,210', description: 'Building Momentum' },
  { timeline: 'Month 6-12', turo: '$2,000', turno: '$4,200', roomster: '$1,000', total: '$7,200', description: 'Scaling Phase' },
  { timeline: 'Year 2+', turo: '$4,000', turno: '$5,500', roomster: '$2,500', total: '$12,000', description: 'Professional Operation' }
];

export const realExample = {
  title: 'Real Example: The First Year',
  phases: [
    {
      phase: 'Month 1-2: Starting Out',
      details: [
        'Turo: Rent first vehicle at $35/day, 15 days/month = $525',
        'Turno: 4 cleaning jobs/week at $100 avg = $1,600/month',
        'Roomster: Rent your spare room for $1,000/month'
      ],
      total: '$3,125/month'
    },
    {
      phase: 'Month 3-4: Building Momentum',
      details: [
        'Turo: Now hosting your first vehicle, 18 days/month at $45 = $810/month',
        'Turno: 6 cleaning jobs/week at $120 avg = $2,400/month',
        'Roomster: $1,000/month (room consistently rented)'
      ],
      total: '$4,210/month'
    },
    {
      phase: 'Month 6-12: Scaling Phase',
      details: [
        'Turo: Two vehicles hosted, 20 days/month each at $50 = $2,000/month',
        'Turno: 7 jobs/week at $160 avg + team management = $4,200/month',
        'Roomster: $1,000/month (possibly expanded to multiple units)'
      ],
      total: '$7,200/month'
    },
    {
      phase: 'Year 2+: Professional Operation',
      details: [
        'Turo: 3-4 vehicles, professional hosting = $3,500-4,500/month',
        'Turno: Team of 2-3 cleaners, you managing = $5,000-6,000/month',
        'Roomster: 2-3 rental units = $2,000-3,000/month'
      ],
      total: '$10,500-13,500/month'
    }
  ]
};

export const investmentRequired = {
  title: 'The Investment Required',
  startup: [
    { item: 'Roomster room listing', cost: '$0', note: '(already have your room)' },
    { item: 'Turno cleaning supplies', cost: '$400-750', note: '' },
    { item: 'Turo vehicle', cost: '$4,000-10,000', note: '(optional; you can start with one you already own)' }
  ],
  totalMin: '$400-750',
  totalMinNote: 'if using existing vehicle',
  totalMax: '$5,500-10,750',
  totalMaxNote: 'if purchasing vehicle',
  comparison: [
    { option: 'Traditional business', cost: '$10,000-50,000+ minimum' },
    { option: 'Getting a second job', cost: '20+ hours/week time cost' },
    { option: 'Real estate investment', cost: '$20,000-100,000+ down payment' }
  ]
};

export const testimonials = [
  {
    id: 1,
    quote: "I wanna say this guide gave me the confidence to start hosting on Turo. I had a 2015 Honda that was just sitting there. Following the exact steps, I'm now earning $1,200/month from that car alone. The financial projections were realistic—I'm hitting my targets.",
    author: 'David M.',
    location: 'Miami',
    rating: 5
  },
  {
    id: 2,
    quote: "I wanna say as a single parent, I needed flexible income. The Turno section showed me I wasn't pricing myself fairly. After following the bidding strategy, I went from $1,800/month to $3,400/month in three months, working the same hours. Game-changer.",
    author: 'Maria S.',
    location: 'Austin',
    rating: 5
  },
  {
    id: 3,
    quote: "I wanna say I've made $18,000 across all three platforms in eight months. The best part? The diversification gives me peace of mind. If one platform has issues, I have two others.",
    author: 'James T.',
    location: 'Denver',
    rating: 5
  }
];

export const faqData = [
  {
    id: 'faq-1',
    question: "Isn't this just a get-rich-quick scheme?",
    answer: "No. This guide is based on conservative estimates and realistic timelines. Most people see meaningful income ($2,000-3,000/month) within 3 months, not weeks. Building to $6,000+ monthly takes consistent effort over 6-12 months."
  },
  {
    id: 'faq-2',
    question: "What if I don't have a vehicle?",
    answer: "You don't need to start with Turo. Many people start with Turno (cleaning) and Roomster (room rental), which require minimal capital. Turo becomes an option once you have savings to invest."
  },
  {
    id: 'faq-3',
    question: "Isn't competition high on these platforms?",
    answer: "Yes, but within each platform, success comes from quality, pricing strategy, and reliability—not luck. This guide shows you exactly how to position yourself above competition."
  },
  {
    id: 'faq-4',
    question: "What if I don't want to do manual work?",
    answer: "This guide includes scaling sections where you hire other cleaners and manage operations. You transition from doing the work to managing the business."
  },
  {
    id: 'faq-5',
    question: "Is this legal?",
    answer: "The guide explicitly covers legal and tax considerations for each platform. It's designed to help you operate ethically and within regulations."
  }
];

export const includedItems = [
  'Comprehensive 100+ Page Strategy Guide (Google Doc format, editable)',
  'Detailed Platform Tutorials for Turo, Turno, and Roomster',
  'Vehicle Acquisition Strategies and Sources',
  'Pricing Frameworks and Bidding Templates',
  'Income Tracking Spreadsheets',
  'Sample Listings and Marketing Language',
  'Month-by-Month Financial Projections',
  'Scale-Up Action Plans',
  'Tax and Legal Considerations',
  'Real-World Case Studies and Examples'
];

export const credibilityBuilders = [
  'Based on real 2025 platform data and actual user experiences',
  'Includes honest financial projections with conservative estimates',
  'Written with practical implementation in mind (not theoretical)',
  'Addresses legal, tax, and regulatory considerations',
  'Provides realistic timelines and actual numbers',
  'Grounded in biblical principles of work and stewardship',
  'Scalable from part-time side income to full-time business',
  'Requires no special skills, credentials, or experience to start'
];

export const pricingJustification = [
  'Less than two professional consulting hours ($150-300)',
  'Less than one month of traditional business courses',
  'Less than a single week of childcare for most families',
  'Less than most people spend on non-essential monthly expenses'
];

export const ctaVariations = {
  strong: "Stop working harder for the same money. Get the complete Turo-Turno-Roomster strategy guide today for $50. You'll have a realistic 12-month plan for $6,000+ monthly income within hours of reading. No waiting, no complicated setup. Start this week.",
  curiosity: "Most people have no idea how much income their idle vehicle, spare time, or empty bedroom could generate. This guide reveals the exact numbers and step-by-step strategy. Discover your income potential for $50.",
  urgency: "Platform rates and policies change. The pricing data and strategies in this guide are based on 2025 rates. Secure your copy today before this information becomes outdated.",
  value: "This guide contains the same financial planning and scaling strategies that consulting firms charge $5,000+ for. Get it for $50 and implement yourself—the ROI is immediate."
};

export const trustBadges = [
  'Based on Real 2025 Data',
  'Honest Financial Projections',
  'Practical Implementation',
  'Legal & Tax Guidance',
  'Money-Back Guarantee'
];

export const marketingImages = {
  hero: 'https://customer-assets.emergentagent.com/job_bfd6c669-c700-44e9-9ec7-53173acc3c4e/artifacts/o3an58xn_7ca3ced2-e62d-4ae2-8150-746eeacffb6f.png',
  bookMockup: 'https://customer-assets.emergentagent.com/job_bfd6c669-c700-44e9-9ec7-53173acc3c4e/artifacts/j9h0dw9a_b71a9344-bb2b-4c60-82f0-920596608fa8.png',
  whatsInside: 'https://customer-assets.emergentagent.com/job_bfd6c669-c700-44e9-9ec7-53173acc3c4e/artifacts/xod8w3wa_315976b0-4426-410a-9d7f-7f426f408d72.png',
  promo: 'https://customer-assets.emergentagent.com/job_bfd6c669-c700-44e9-9ec7-53173acc3c4e/artifacts/0ua1rl6f_dee741ff-e907-4b3d-bfc1-e3e318fb1782.png',
  platforms: 'https://customer-assets.emergentagent.com/job_bfd6c669-c700-44e9-9ec7-53173acc3c4e/artifacts/jx37eilw_e5725fab-da6a-4ca1-a42b-db71e398c993.png',
  // New images
  multiDevice: 'https://customer-assets.emergentagent.com/job_bfd6c669-c700-44e9-9ec7-53173acc3c4e/artifacts/x2zlj7wa_f77f5d4d-e9a3-4353-8bf2-f7de29397f36.png',
  bookCover: 'https://customer-assets.emergentagent.com/job_bfd6c669-c700-44e9-9ec7-53173acc3c4e/artifacts/qyuxs25q_59ca0fe1-4451-42b4-af46-0388d6df6d77.png',
  chapterPreview: 'https://customer-assets.emergentagent.com/job_bfd6c669-c700-44e9-9ec7-53173acc3c4e/artifacts/n2e46c06_68db866a-6d27-4b4d-9906-accd26177a1b.png',
  roi: 'https://customer-assets.emergentagent.com/job_bfd6c669-c700-44e9-9ec7-53173acc3c4e/artifacts/zpv39dfw_917c9f67-0757-4384-8a32-b42630b4143a.png',
  faithBased: 'https://customer-assets.emergentagent.com/job_bfd6c669-c700-44e9-9ec7-53173acc3c4e/artifacts/7v80styk_bedb1cbd-fe4e-4e40-9e82-22628b6fa125.png'
};

export const navLinks = [
  { name: 'Home', href: '#home' },
  { name: 'About', href: '#about' },
  { name: 'Features', href: '#features' },
  { name: 'FAQ', href: '#faq' },
  { name: 'Contact', href: '#contact' }
];

export const finalSummary = {
  headline: "Turn Your Assets Into Income Streams",
  subheadline: "The Proven Three-Platform Strategy for Financial Stability and Long-Term Wealth Building",
  description: "This is not a get-rich-quick scheme. This is not theoretical. This is a practical, step-by-step guide based on real 2025 platform data showing how to generate $6,000+ monthly income through Turo vehicle hosting, Turno professional cleaning, and Roomster room rentals.",
  closing: "You'll get realistic financial projections, detailed implementation steps, pricing strategies, scaling roadmaps, and tax guidance. All for $50.",
  ps: "P.S. Your first $50 investment in this guide could translate to $2,000-6,000 additional monthly income within 6 months. That's a 40-120x ROI in just half a year. Worth the investment? We think so."
};
